import { signal, WritableSignal, Signal, EffectRef } from '@angular/core';
import { Ref } from '../providers/ref';

/**
 * Tipo que permite objetos O tipos primitivos
 */
export type StateType = Record<string, any> | boolean | number | string;

export abstract class BaseNotifier<T extends StateType> {
  protected _state: WritableSignal<T>;
  protected readonly ref: Ref;
  private readonly _initialState: T;
  private readonly _cleanups: Array<() => void> = [];
  protected _disposed = false;

  constructor(ref: Ref, initialState: T) {
    this.ref = ref;
    this._initialState = initialState;
    this._state = signal<T>(initialState as T);
    // Disparamos la construcción inicial del estado
    this.build();
  }

  get state(): Signal<T> {
    return this._state.asReadonly();
  }

  /**
   * snapshot() — Lee estado SIN crear dependencia (one-shot).
   * Usar SIEMPRE dentro de effects o métodos llamados desde effects.
   * Equivalente a Riverpod's ref.read(provider).
   */
  protected snapshot(): T {
    return this.ref.read(this._state);
  }

  /**
   * Actualiza el estado completamente
   */
  protected setState(newState: T): void {
    this._state.set(newState);
  }

  /**
   * Actualiza el estado basado en el valor anterior
   */
  protected updateState(updater: (prevState: T) => T): void {
    this._state.update(updater);
  }

  /**
   * Restaura el estado inicial (usado al cerrar sesión).
   * Los notifiers cuyo estado inicial contenga objetos/colecciones
   * compartidas deben sobreescribirlo clonando.
   */
  resetState(): void {
    this._state.set(this._initialState);
  }

  // ── Ciclo de vida / limpieza automática ──────────────────────────────

  /**
   * Registra una función de limpieza que se ejecutará en dispose() (orden LIFO).
   * Idempotente: si el notifier ya fue destruido, ejecuta la limpieza al instante.
   */
  protected trackDisposable(cleanup: () => void): void {
    if (this._disposed) {
      cleanup();
      return;
    }
    this._cleanups.push(cleanup);
  }

  /**
   * Registra un handle con método `.remove()` (reactiveUtils.watch, view.on…).
   */
  protected trackHandle(handle: { remove(): void } | null | undefined): void {
    if (handle) this.trackDisposable(() => handle.remove());
  }

  /**
   * Igual que ref.listen, pero ancla el effect al ciclo de vida de este
   * notifier: dispose() destruye el effect automáticamente.
   */
  protected listen(fn: () => void): EffectRef;
  protected listen<S>(target: Signal<S>, callback: (previous: S, next: S) => void): EffectRef;
  protected listen(arg: any, callback?: any): EffectRef {
    const effectRef = (this.ref.listen as (arg: any, callback?: any) => EffectRef)(arg, callback);
    this.trackDisposable(() => effectRef.destroy());
    return effectRef;
  }

  /**
   * Limpieza genérica e idempotente del notifier.
   * Se invoca automáticamente desde BaseProvider.ngOnDestroy al salir de la
   * ruta / cerrar sesión. Ejecuta los cleanups registrados (LIFO) y el hook
   * onDispose() de la subclase.
   */
  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;

    const cleanups = this._cleanups.splice(0);
    for (let i = cleanups.length - 1; i >= 0; i--) {
      try {
        cleanups[i]();
      } catch {
        // Un cleanup con error no debe impedir el resto.
      }
    }

    try {
      this.onDispose();
    } catch {
      // Ídem.
    }
  }

  /**
   * Hook de subclase para limpieza extra al destruir (timers, views de ArcGIS…).
   */
  protected onDispose(): void {
    // Override en subclases.
  }

  /**
   * Build method - Called automatically when provider is first created
   * Override this method to initialize state with dependencies
   *
   * Equivalent to Riverpod's build() method
   * Puede ser sync o async
   */
  build(): T | void | Promise<T | void> {
    // Override in subclasses to initialize state with dependencies
  }
}
